import type { ExperimentCategory } from "@andrum/schemas";
import { scienceNoteForSlug } from "@/i18n/catalog";
import { t } from "@/i18n";

/**
 * Soft “why this may help” appendix for coach replies that steer to an experiment.
 */
export function coachWhyAppendix(opts: {
  category?: ExperimentCategory;
  slug?: string;
}): string {
  if (opts.slug && opts.category) {
    const localized = scienceNoteForSlug(opts.slug);
    const why =
      localized?.why ?? t(`learn.categories.${opts.category}.headline`);
    return `\n\n${t("coach.whyPrefix", { text: why })}\n\n${t("coach.learnMore")}`;
  }
  if (opts.category) {
    const headline = t(`learn.categories.${opts.category}.headline`);
    const mechanism = t(`learn.categories.${opts.category}.m1`);
    return `\n\n${t("coach.whyCategory", {
      headline,
      mechanism,
    })}\n\n${t("coach.learnMore")}`;
  }
  return `\n\n${t("coach.whyDefault")}`;
}

/** Map free-text intent to a category for local explainers */
export function categoryFromCoachIntent(text: string): ExperimentCategory | null {
  const lower = text.toLowerCase();
  if (
    lower.includes("stress") ||
    lower.includes("orolig") ||
    lower.includes("ångest") ||
    lower.includes("spänd")
  ) {
    return "recovery";
  }
  if (
    lower.includes("energi") ||
    lower.includes("trött") ||
    lower.includes("pigg") ||
    lower.includes("lite energi") ||
    lower.includes("energy") ||
    lower.includes("tired")
  ) {
    return "movement";
  }
  if (
    lower.includes("ensam") ||
    lower.includes("närhet") ||
    lower.includes("social") ||
    lower.includes("kontakt") ||
    lower.includes("lonely") ||
    lower.includes("connection")
  ) {
    return "connection";
  }
  if (
    lower.includes("tacksam") ||
    lower.includes("glädje") ||
    lower.includes("grateful") ||
    lower.includes("gratitude")
  ) {
    return "gratitude";
  }
  if (
    lower.includes("närvaro") ||
    lower.includes("fokus") ||
    lower.includes("skärm") ||
    lower.includes("mindful") ||
    lower.includes("presence")
  ) {
    return "presence";
  }
  if (
    lower.includes("mening") ||
    lower.includes("värde") ||
    lower.includes("riktning") ||
    lower.includes("meaning")
  ) {
    return "meaning";
  }
  if (
    lower.includes("välja") ||
    lower.includes("förslag") ||
    lower.includes("något litet") ||
    lower.includes("experiment") ||
    lower.includes("choose") ||
    lower.includes("something small")
  ) {
    return "recovery";
  }
  return null;
}

export function wantsExperimentHelp(text: string): boolean {
  const lower = text.toLowerCase();
  return (
    lower.includes("välja") ||
    lower.includes("förslag") ||
    lower.includes("något litet") ||
    lower.includes("experiment") ||
    lower.includes("stress") ||
    lower.includes("energi") ||
    lower.includes("vad ska jag") ||
    lower.includes("hjälp mig") ||
    lower.includes("help me") ||
    lower.includes("choose") ||
    lower.includes("something small")
  );
}
